import React, { useEffect, useState } from 'react';
import { obtenerReservas, cambiarEstadoReserva, reagendarReserva, eliminarReserva } from '../api/reservas';
import { obtenerManicuristas } from '../api/manicuristas';
import { useNotify } from '../components/NotificationProvider';
import { extractHHmm, to12hWithAmPm } from '../utils/time';
import { isAdminRole, isSuperAdminRole } from '../utils/helpers';

// Convierte 'YYYY-MM-DD' o ISO 'YYYY-MM-DDTHH:mm:ssZ' a 'DD/MM/YYYY' sin aplicar zona horaria
function toDDMMYYYY(value) {
  if (!value) return '—';
  const s = String(value);
  const ymd = s.includes('T') ? s.split('T')[0] : s; // 'YYYY-MM-DD'
  const [y, m, d] = (ymd || '').split('-');
  if (!y || !m || !d) return ymd || '—';
  return `${d}/${m}/${y}`;
}

// Si sigues usando este formateador local, puedes delegar al util (recomendado unificar):
function formatTimeAMPM(timeStr) {
  const hhmm = extractHHmm(timeStr);
  return to12hWithAmPm(hhmm);
}

const statusLabels = {
  pendiente: 'Pendiente',
  confirmada: 'Confirmada',
  cancelada: 'Cancelada',
  reagendada: 'Reagendada'
};

const statusColors = {
  pendiente: 'bg-yellow-100 text-yellow-800',
  confirmada: 'bg-green-100 text-green-800',
  cancelada: 'bg-red-100 text-red-800',
  reagendada: 'bg-indigo-100 text-indigo-800'
};

const AdminBookings = ({ user, onLoginClick }) => {
  const [reservas, setReservas] = useState([]);
  // Llamar al hook SIEMPRE en el cuerpo del componente, no dentro de useEffect
  const { error: notifyError, success: notifySuccess } = useNotify();
  const [reagendarReservaSel, setReagendarReservaSel] = useState(null);
  const [mensajeReagendar, setMensajeReagendar] = useState('');
  const [savingReagendar, setSavingReagendar] = useState(false);
  const [notaSel, setNotaSel] = useState(null);
  const [mensajeSel, setMensajeSel] = useState(null);
  const [calificacionSel, setCalificacionSel] = useState(null);
  const [pendingDeleteReserva, setPendingDeleteReserva] = useState(null);

  // Paginación
  const PAGE_SIZE = 5;
  const [page, setPage] = useState(1);

  // Filtros
  const [manicuristas, setManicuristas] = useState([]);
  const [manicuristaFilter, setManicuristaFilter] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');

  // Normaliza diferentes formatos de hora a 'HH:mm' y permite deduplicar y ordenar
  const normalizeSlot = (val) => {
    if (!val) return null;
    const s = String(val).trim();
    // ISO o datetime -> extraer HH:mm
    if (s.includes('T')) return extractHHmm(s);
    // HH:mm:ss o HH:mm
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) return s.slice(0, 5);
    // Intentar con Date parseable
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    }
    return null;
  };

  const sortHHmmAsc = (arr) => {
    const toMinutes = (t) => {
      const [h, m] = String(t).split(':').map(Number);
      return (h || 0) * 60 + (m || 0);
    };
    return [...arr].sort((a, b) => toMinutes(a) - toMinutes(b));
  };

  useEffect(() => {
    const cargarManicuristas = async () => {
      try {
        const data = await obtenerManicuristas(user);
        setManicuristas(Array.isArray(data) ? data : []);
      } catch (error) {
        // Silencioso: no bloquear la grilla si falla
      }
    };
    if (isSuperAdminRole(user?.role)) cargarManicuristas();
  }, [user?.id, user?.role]);

  useEffect(() => {
    const cargarReservas = async () => {
      try {
        const data = await obtenerReservas(user);
        // Ordenar: pendientes primero, luego por Fecha+Hora (más reciente primero)
        const toTs = (r) => {
          let base = String(r.Fecha || '').trim();
          let hora = String(r.Hora || '').trim();
          if (base && hora) {
            const dt = new Date(`${base}T${hora.length===5?hora+':00':hora}`);
            if (!isNaN(dt.getTime())) return dt.getTime();
          }
          // Fallback: solo fecha
          if (base) {
            const d = new Date(base);
            if (!isNaN(d.getTime())) return d.getTime();
          }
          return 0;
        };
        const sorted = [...data].sort((a,b)=> {
          // Pendientes siempre arriba
          const aPendiente = a.Estado === 'pendiente' ? 1 : 0;
          const bPendiente = b.Estado === 'pendiente' ? 1 : 0;
          if (aPendiente !== bPendiente) return bPendiente - aPendiente;
          // Mismo estado: ordenar por fecha/hora
          return toTs(b) - toTs(a);
        });
        setReservas(sorted);
      } catch (error) {
        notifyError('Error al cargar reservas: ' + error.message);
      }
    };
    cargarReservas();
  }, [user?.id, user?.role, user?.email, user?.telefono]);

  useEffect(() => {
    setPage(1);
  }, [manicuristaFilter, estadoFilter]);

  const reservasFiltradas = reservas.filter((r) => {
    const matchManicurista = !manicuristaFilter || String(r.ManicuristaId) === String(manicuristaFilter);
    const matchEstado = !estadoFilter || String(r.Estado).toLowerCase() === String(estadoFilter).toLowerCase();
    return matchManicurista && matchEstado;
  });

  if (!user || !isAdminRole(user.role)) {
    return (
      <div className="text-center py-20 bg-white rounded-3xl shadow-xl">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Acceso Restringido</h2>
        <p className="text-gray-600 mb-6">
          Solo los administradores tienen acceso a esta sección. Por favor, inicia sesión con una cuenta de administrador.
        </p>
        <button
          onClick={onLoginClick}
          className="px-8 py-3 bg-purple-400 text-white font-bold rounded-xl shadow-lg hover:bg-purple-500 transition-all duration-300"
        >
          Iniciar Sesión
        </button>
      </div>
    );
  }

  const handleStatusChange = async (id, estado) => {
    // Actualización optimista: solo afecta a la fila clickeada
    const prev = reservas;
    setReservas((rs) => rs.map(r => r.Id === id ? { ...r, Estado: estado } : r));
    try {
      await cambiarEstadoReserva(id, estado);
      notifySuccess('Estado actualizado a ' + estado);
      // Opcional: refrescar silenciosamente y mantener el orden estable
      try {
        const data = await obtenerReservas(user);
        const toTs = (r) => {
          const creado = r.CreadoEn || r.creadoEn || null;
          if (creado) {
            const d = new Date(creado);
            if (!isNaN(d.getTime())) return d.getTime();
          }
          let base = String(r.Fecha || '').trim();
          let hora = String(r.Hora || '').trim();
          if (base && hora) {
            const dt = new Date(`${base}T${hora.length===5?hora+':00':hora}`);
            if (!isNaN(dt.getTime())) return dt.getTime();
          }
          return Number(r.Id || 0);
        };
        setReservas([...data].sort((a,b)=> toTs(b) - toTs(a)));
      } catch (_) { /* si falla el refresh, mantenemos el optimista */ }
    } catch (error) {
      // Revertir si falla
      setReservas(prev);
      notifyError('Error al cambiar estado: ' + error.message);
    }
  };

  // Abrir modal de posponer en lugar de cambiar estado directo
  const openReagendarModal = (reserva) => {
    setReagendarReservaSel(reserva);
    setMensajeReagendar('');
  };

  // Reagendar ya no obtiene slots; solo envía un mensaje y libera la hora

  const submitReagendar = async () => {
    if (!reagendarReservaSel) return;
    try {
      setSavingReagendar(true);
      await reagendarReserva(reagendarReservaSel.Id, { Mensaje: mensajeReagendar || 'Hora cancelada por motivos personales. Vuelve a reservar.' });
      notifySuccess('Reserva marcada como reagendada y hora liberada');
      setReagendarReservaSel(null);
      const data = await obtenerReservas(user);
      setReservas(data);
    } catch (e) {
      const detalle = e?.status ? ` (HTTP ${e.status})` : '';
      notifyError('Error al reagendar: ' + e.message + detalle);
    } finally {
      setSavingReagendar(false);
    }
  };

  const closeReagendarModal = () => setReagendarReservaSel(null);

  const handleDeleteReserva = (reserva) => setPendingDeleteReserva(reserva);
  const cancelDeleteReserva = () => setPendingDeleteReserva(null);

  const confirmDeleteReserva = async () => {
    if (!pendingDeleteReserva) return;
    try {
      await eliminarReserva(pendingDeleteReserva.Id, user);
      setReservas((prev) => prev.filter((r) => r.Id !== pendingDeleteReserva.Id));
      notifySuccess('Reserva eliminada');
    } catch (error) {
      notifyError('Error al eliminar reserva: ' + error.message);
    } finally {
      setPendingDeleteReserva(null);
    }
  };

  return (
    // Breakout: ancho de viewport y márgenes negativos para romper el container superior
    <div className="w-screen max-w-none -mx-[calc((100vw-100%)/2)] px-2 md:px-8 py-6"> {/* full-bleed */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-purple-700">Gestion de Reservas</h1>
        <p className="mt-1 text-sm text-gray-600">
          {isSuperAdminRole(user.role)
            ? 'Vista completa de todas las reservas.'
            : 'Vista filtrada: solo reservas asignadas a tu manicurista.'}
        </p>
      </div>

      {/* Filtros */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        {isSuperAdminRole(user.role) && (
          <select
            value={manicuristaFilter}
            onChange={(e) => setManicuristaFilter(e.target.value)}
            className="h-10 rounded-xl border-2 border-purple-200 px-3 text-sm text-gray-700 focus:border-pink-500 focus:outline-none bg-white"
          >
            <option value="">Todas las manicuristas</option>
            {manicuristas.map((m) => (
              <option key={m.Id} value={m.Id}>{m.Nombre}</option>
            ))}
          </select>
        )}
        <select
          value={estadoFilter}
          onChange={(e) => setEstadoFilter(e.target.value)}
          className="h-10 rounded-xl border-2 border-purple-200 px-3 text-sm text-gray-700 focus:border-pink-500 focus:outline-none bg-white"
        >
          <option value="">Todos los estados</option>
          {Object.entries(statusLabels).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {/* VISTA DESKTOP: Tabla */}
      <div className="hidden md:block w-full max-w-none rounded-xl border border-purple-200 shadow-lg bg-white overflow-hidden">
        <table className="w-full table-fixed divide-y divide-purple-100 text-xs">
          <colgroup>
            <col style={{ width: '6.5rem' }} />
            <col style={{ width: '8rem' }} />
            <col style={{ width: '7rem' }} />
            <col style={{ width: '9rem' }} />
            <col style={{ width: '8rem' }} />
            <col style={{ width: '6rem' }} />
            <col style={{ width: '6rem' }} />
            <col style={{ width: '6.5rem' }} />
            <col style={{ width: '6rem' }} />
            <col style={{ width: '10rem' }} />
            <col style={{ width: '6rem' }} />
            <col style={{ width: '13rem' }} />
          </colgroup>
          <thead className="bg-purple-50">
            <tr>
              <th className="px-2 py-2 font-bold text-purple-700 text-left text-xs">N° Reserva</th>
              <th className="px-2 py-2 font-bold text-purple-700 text-left text-xs">Nombre</th>
              <th className="px-2 py-2 font-bold text-purple-700 text-left text-xs">Teléfono</th>
              <th className="px-2 py-2 font-bold text-purple-700 text-left text-xs">Producto</th>
              <th className="px-2 py-2 font-bold text-purple-700 text-left text-xs">Manicurista</th>
              <th className="px-2 py-2 font-bold text-purple-700 text-left text-xs">Fecha</th>
              <th className="px-2 py-2 font-bold text-purple-700 text-left text-xs">Hora</th>
              <th className="px-2 py-2 font-bold text-purple-700 text-left text-xs">Estado</th>
              <th className="px-2 py-2 font-bold text-purple-700 text-left text-xs">Notas</th>
              <th className="px-2 py-2 font-bold text-purple-700 text-left text-xs">Msg Reagend.</th>
              <th className="px-2 py-2 font-bold text-purple-700 text-left text-xs">Rating</th>
              <th className="px-2 py-2 font-bold text-purple-700 text-left text-xs">Acciones</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-purple-100">
            {reservasFiltradas.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE).map(reserva => (
              <tr key={reserva.Id} className="align-top">
                <td className="px-2 py-2 break-words">
                  <span className="inline-block px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs font-semibold border border-indigo-200">
                    {reserva.NumeroReserva || reserva.numeroReserva || reserva.Id || '—'}
                  </span>
                </td>
                <td className="px-2 py-2 break-all">{reserva.NombreCliente || '—'}</td>
                <td className="px-2 py-2 break-words">{reserva.UsuarioTelefono || reserva.Telefono || '—'}</td>
                <td className="px-2 py-2 break-words">{reserva.NombreProducto || reserva.Producto || '—'}</td>
                <td className="px-2 py-2 break-words">{reserva.NombreManicurista || '-'}</td>
                <td className="px-2 py-2 break-words">{toDDMMYYYY(reserva.Fecha)}</td>
                <td className="px-2 py-2 break-words">{reserva.Hora ? formatTimeAMPM(reserva.Hora) : '—'}</td>
                <td className="px-2 py-2">
                  <span className={`inline-block px-1.5 py-0.5 rounded font-semibold text-xs ${statusColors[reserva.Estado]}`}>
                    {statusLabels[reserva.Estado] || reserva.Estado || '—'}
                  </span>
                </td>
                {/* Columna Notas con botón para abrir modal */}
                <td className="px-2 py-2">
                  <button
                    className="px-1.5 py-0.5 rounded text-xs border border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-50"
                    disabled={!reserva.Notas}
                    onClick={()=>setNotaSel(reserva.Notas)}
                  >Ver nota</button>
                </td>
                <td className="px-2 py-2">
                  <button
                    className="px-1.5 py-0.5 rounded text-xs border border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-50"
                    disabled={!reserva.MensajeReagendamiento}
                    onClick={()=>setMensajeSel(reserva.MensajeReagendamiento)}
                  >Ver estado</button>
                </td>
                <td className="px-2 py-2">
                  {reserva.Calificacion ? (
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: reserva.Calificacion }).map((_, i) => (
                          <span key={i} className="text-yellow-400 text-xs">★</span>
                        ))}
                        {Array.from({ length: 5 - reserva.Calificacion }).map((_, i) => (
                          <span key={i} className="text-gray-300 text-xs">★</span>
                        ))}
                      </div>
                      <button
                        className="px-1.5 py-0.5 rounded text-xs border border-purple-200 text-purple-700 hover:bg-purple-50"
                        onClick={() => setCalificacionSel({ estrellas: reserva.Calificacion, mensaje: reserva.ComentarioCalificacion || 'Sin comentarios' })}
                      >
                        Ver eval.
                      </button>
                    </div>
                  ) : (
                    <span className="text-gray-400 text-xs">Sin evaluar</span>
                  )}
                </td>
                <td className="px-2 py-2">
                  <div className="flex gap-1 whitespace-nowrap flex-wrap">
                    {/* Confirmada */}
                    <button type="button"
                      className={`px-2 py-0.5 rounded text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                        ${reserva.Estado === 'confirmada'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}
                      `}
                      disabled={reserva.Estado === 'confirmada' || reserva.Estado === 'cancelada'}
                      onClick={(e) => { e.stopPropagation(); handleStatusChange(reserva.Id, 'confirmada'); }}
                    >
                      Aceptar
                    </button>

                    {/* Cancelada */}
                    <button type="button"
                      className={`px-2 py-0.5 rounded text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                        ${reserva.Estado === 'cancelada'
                          ? 'bg-rose-600 text-white'
                          : 'bg-rose-100 text-rose-700 hover:bg-rose-200'}
                      `}
                      disabled={reserva.Estado === 'cancelada' || reserva.Estado === 'confirmada'}
                      onClick={(e) => { e.stopPropagation(); handleStatusChange(reserva.Id, 'cancelada'); }}
                    >
                      Rechazar
                    </button>

                    {/* Reagendar */}
                    <button type="button"
                      className={`px-2 py-0.5 rounded text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                        ${reserva.Estado === 'reagendada'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'}
                      `}
                      disabled={reserva.Estado === 'reagendada'}
                      onClick={(e) => { e.stopPropagation(); openReagendarModal(reserva); }}
                    >
                      Reagendar
                    </button>

                    {/* Eliminar (solo superadmin) */}
                    {isSuperAdminRole(user?.role) && (
                      <button type="button"
                        className="px-2 py-0.5 rounded text-xs bg-rose-100 text-rose-700 hover:bg-rose-200 transition-colors"
                        onClick={(e) => { e.stopPropagation(); handleDeleteReserva(reserva); }}
                      >
                        Eliminar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}

            {reservasFiltradas.length === 0 && (
              <tr>
                <td colSpan={12} className="px-3 py-10 text-center text-purple-500 font-semibold">
                  No hay reservas para mostrar.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* VISTA MÓVIL: Tarjetas */}
      <div className="md:hidden space-y-4">
        {reservasFiltradas.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE).map(reserva => (
          <div key={reserva.Id} className="bg-white rounded-xl border border-purple-200 shadow-lg p-4 space-y-3">
            {/* Encabezado: N° y Estado */}
            <div className="flex justify-between items-start gap-2">
              <span className="inline-block px-2 py-1 rounded bg-indigo-50 text-indigo-700 text-xs font-semibold border border-indigo-200">
                {reserva.NumeroReserva || reserva.Id || '—'}
              </span>
              <span className={`inline-block px-2 py-1 rounded font-semibold text-xs ${statusColors[reserva.Estado]}`}>
                {statusLabels[reserva.Estado] || reserva.Estado || '—'}
              </span>
            </div>

            {/* Datos principales */}
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-semibold text-purple-700">Nombre:</span> {reserva.NombreCliente || '—'}
              </div>
              <div>
                <span className="font-semibold text-purple-700">Teléfono:</span> {reserva.UsuarioTelefono || reserva.Telefono || '—'}
              </div>
              <div>
                <span className="font-semibold text-purple-700">Producto:</span> {reserva.NombreProducto || '—'}
              </div>
              <div>
                <span className="font-semibold text-purple-700">Manicurista:</span> {reserva.NombreManicurista || '-'}
              </div>
              <div>
                <span className="font-semibold text-purple-700">Fecha/Hora:</span> {toDDMMYYYY(reserva.Fecha)} {reserva.Hora ? formatTimeAMPM(reserva.Hora) : '—'}
              </div>
            </div>

            {/* Rating */}
            {reserva.Calificacion && (
              <div>
                <span className="font-semibold text-purple-700 text-sm">Rating:</span>
                <div className="flex items-center gap-1 mt-1">
                  {Array.from({ length: reserva.Calificacion }).map((_, i) => (
                    <span key={i} className="text-yellow-400">★</span>
                  ))}
                  {Array.from({ length: 5 - reserva.Calificacion }).map((_, i) => (
                    <span key={i} className="text-gray-300">★</span>
                  ))}
                </div>
                <button
                  className="mt-1 px-2 py-1 rounded text-xs border border-purple-200 text-purple-700 hover:bg-purple-50"
                  onClick={() => setCalificacionSel({ estrellas: reserva.Calificacion, mensaje: reserva.ComentarioCalificacion || 'Sin comentarios' })}
                >
                  Ver evaluación
                </button>
              </div>
            )}

            {/* Botones de acción */}
            <div className="flex flex-col gap-2 pt-2">
              <button
                className={`w-full px-3 py-2 rounded text-xs font-semibold transition-colors ${
                  reserva.Estado === 'confirmada'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                } ${reserva.Estado === 'confirmada' || reserva.Estado === 'cancelada' ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={reserva.Estado === 'confirmada' || reserva.Estado === 'cancelada'}
                onClick={() => handleStatusChange(reserva.Id, 'confirmada')}
              >
                Aceptar
              </button>
              <button
                className={`w-full px-3 py-2 rounded text-xs font-semibold transition-colors ${
                  reserva.Estado === 'cancelada'
                    ? 'bg-rose-600 text-white'
                    : 'bg-rose-100 text-rose-700 hover:bg-rose-200'
                } ${reserva.Estado === 'cancelada' || reserva.Estado === 'confirmada' ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={reserva.Estado === 'cancelada' || reserva.Estado === 'confirmada'}
                onClick={() => handleStatusChange(reserva.Id, 'cancelada')}
              >
                Rechazar
              </button>
              <button
                className={`w-full px-3 py-2 rounded text-xs font-semibold transition-colors ${
                  reserva.Estado === 'reagendada'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                } ${reserva.Estado === 'reagendada' ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={reserva.Estado === 'reagendada'}
                onClick={() => openReagendarModal(reserva)}
              >
                Reagendar
              </button>

              {isSuperAdminRole(user?.role) && (
                <button
                  className="w-full px-3 py-2 rounded text-xs font-semibold bg-rose-100 text-rose-700 hover:bg-rose-200 transition-colors"
                  onClick={() => handleDeleteReserva(reserva)}
                >
                  Eliminar
                </button>
              )}
            </div>
          </div>
        ))}

        {reservasFiltradas.length === 0 && (
          <div className="text-center py-10 text-purple-500 font-semibold">
            No hay reservas para mostrar.
          </div>
        )}
      </div>
      {reagendarReservaSel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl border border-purple-200">
            <h2 className="text-xl font-bold mb-4 text-purple-700">Reagendar (liberar hora)</h2>
            <p className="text-sm text-gray-600 mb-4">
              Reserva N° <strong>{reagendarReservaSel.NumeroReserva || reagendarReservaSel.Id}</strong><br />
              Servicio: <strong>{reagendarReservaSel.NombreProducto || 'Servicio'}</strong><br />
              Duración: <strong>{reagendarReservaSel.Duracion ? `${reagendarReservaSel.Duracion} min` : '—'}</strong><br />
              Cliente: <strong>{reagendarReservaSel.NombreCliente || '—'}</strong>
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-purple-700 mb-1">Mensaje al Cliente</label>
                <textarea
                  rows={3}
                  value={mensajeReagendar}
                  onChange={(e) => setMensajeReagendar(e.target.value)}
                  placeholder="Ej: Hora cancelada por motivos personales. Vuelve a reservar."
                  className="w-full border-2 border-purple-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={closeReagendarModal}
                className="px-4 py-2 rounded-xl bg-gray-200 text-gray-700 hover:bg-gray-300"
              >Cancelar</button>
              <button
                onClick={submitReagendar}
                disabled={savingReagendar}
                className="px-4 py-2 rounded-xl bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
              >Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {pendingDeleteReserva && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl border border-purple-200">
            <h3 className="text-lg font-bold text-purple-700 mb-2">Eliminar reserva</h3>
            <p className="text-sm text-gray-600 mb-4">
              ¿Seguro que deseas eliminar la reserva N° <strong>{pendingDeleteReserva.NumeroReserva || pendingDeleteReserva.Id}</strong>?<br />
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelDeleteReserva}
                className="px-4 py-2 rounded-xl bg-gray-200 text-gray-700 hover:bg-gray-300"
              >Cancelar</button>
              <button
                onClick={confirmDeleteReserva}
                className="px-4 py-2 rounded-xl bg-rose-600 text-white hover:bg-rose-700"
              >Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {notaSel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl border border-purple-200">
            <h3 className="text-lg font-bold text-purple-700 mb-3">Nota de la Reserva</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{notaSel}</p>
            <div className="text-right mt-4">
              <button onClick={()=>setNotaSel(null)} className="px-4 py-2 rounded-xl bg-purple-600 text-white hover:bg-purple-700">Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {mensajeSel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl border border-purple-200">
            <h3 className="text-lg font-bold text-purple-700 mb-3">Mensaje enviado, reagendamiento al Cliente</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{mensajeSel}</p>
            <div className="text-right mt-4">
              <button onClick={()=>setMensajeSel(null)} className="px-4 py-2 rounded-xl bg-purple-600 text-white hover:bg-purple-700">Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {calificacionSel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl border border-purple-200">
            <h3 className="text-lg font-bold text-purple-700 mb-4">Evaluación del Cliente</h3>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm font-semibold text-gray-700">Calificación:</span>
              <div className="flex gap-1">
                {Array.from({ length: calificacionSel.estrellas }).map((_, i) => (
                  <span key={i} className="text-yellow-400 text-xl">★</span>
                ))}
                {Array.from({ length: 5 - calificacionSel.estrellas }).map((_, i) => (
                  <span key={i} className="text-gray-300 text-xl">★</span>
                ))}
              </div>
            </div>
            <div>
              <span className="text-sm font-semibold text-gray-700 block mb-2">Comentario:</span>
              <p className="text-gray-700 whitespace-pre-wrap bg-purple-50 p-3 rounded-lg">{calificacionSel.mensaje}</p>
            </div>
            <div className="text-right mt-4">
              <button onClick={()=>setCalificacionSel(null)} className="px-4 py-2 rounded-xl bg-purple-600 text-white hover:bg-purple-700">Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* Paginación */}
      {reservasFiltradas.length > PAGE_SIZE && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button disabled={page===1} onClick={()=>setPage(p=>Math.max(1,p-1))} className="px-3 py-1 rounded border bg-white disabled:opacity-50">Anterior</button>
          <span className="text-sm text-gray-600">Página {page} de {Math.ceil(reservasFiltradas.length / PAGE_SIZE)}</span>
          <button disabled={page>=Math.ceil(reservasFiltradas.length/PAGE_SIZE)} onClick={()=>setPage(p=>Math.min(Math.ceil(reservasFiltradas.length/PAGE_SIZE),p+1))} className="px-3 py-1 rounded border bg-white disabled:opacity-50">Siguiente</button>
        </div>
      )}
    </div>
  );
};

export default AdminBookings;

// Inyectar modal al DOM principal
// (colocado fuera del return principal para mantener estructura simple)
// Como se usa estado local del componente, debería estar dentro del mismo JSX;
// movemos el modal dentro del retorno principal.
